import React from "react";
import { cloneDeep } from "lodash";
import { Button, Divider, Grid, TextField, Tooltip } from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

import GridListItem from "components/common/grid/GridListItem";
import PatientDiagnosis from "./PatientDiagnosis/PatientDiagnosis";
import usePatientHistoryInformation from "./usePatientHistoryInformation";

import { isValidArray } from "utils/array";
import { formatDateTime } from "utils/date";
import { capitalizeFirstLetter, truncate } from "utils/string";
import {
  ANTICOAGULANT_STATUS_COLOR_MAP,
  STROKE_HISTORY_STATUS_COLOR_MAP,
} from "static/constants";
import AnticoagulantHistory from "components/common/anticoagulant-history/AnticoagulantHistory";
import SubmitButton from "components/Button/SubmitButton";
import RefusesOAC from "components/common/refusesOAC/RefusesOAC";
import ConfirmableToggleButtons from "components/common/toggle_button/ConfirmableToggleButtons";

const PatientHistoryForm = ({
  isLoading,
  isEditMode,
  setIsEditMode,
  handleCancel,
  handleSubmit,
}) => {
  const { handleInputChange, newPatientHistoryInformation, getAnticoagStatus } =
    usePatientHistoryInformation();

  const getAnticoagStatusValue = () => {
    const { anticoagStatus, anticoag } = newPatientHistoryInformation;
    let anticoagStatusValue = anticoagStatus?.toString();

    if (!anticoagStatus) {
      anticoagStatusValue = getAnticoagStatus(anticoag).toString();
    }

    const anticoagDisplay = capitalizeFirstLetter(anticoagStatusValue) || "";

    return anticoagDisplay;
  };

  return (
    <>
      <Grid container rowSpacing={2}>
        {isEditMode ? (
          <Grid container>
            <div className="patient-form__row">
              <div
                className="patient-form__info--header"
                style={{
                  color: "#175EC8",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="mr-2">ANTICOAGULANT</span>
                {(newPatientHistoryInformation?.anticoagUpdatedAt ||
                  newPatientHistoryInformation?.anticoagUpdatedBy) && (
                  <AnticoagulantHistory
                    updatedAt={newPatientHistoryInformation?.anticoagUpdatedAt}
                    updatedBy={newPatientHistoryInformation?.anticoagUpdatedBy}
                  />
                )}
              </div>

              <Grid item container sx={{ paddingBottom: "5px" }} spacing={1}>
                <Grid item>
                  <Grid item>
                    <ConfirmableToggleButtons
                      value={newPatientHistoryInformation.anticoagStatus}
                      color="primary"
                      onChange={(event) => {
                        const copy = cloneDeep(newPatientHistoryInformation);
                        const value = event.target.value;

                        if (value !== "no" && copy.refusesOAC) {
                          copy.refusesOAC = false;
                        }

                        copy.anticoagStatus = value;
                        handleInputChange(copy);
                      }}
                      showConfirmWhen={(newValue) =>
                        newPatientHistoryInformation.refusesOAC &&
                        newValue !== "no"
                      }
                      confirmTitle="Confirm Status Change"
                      confirmMessage={
                        <>
                          Changing the status will update the
                          <b> Anticoagulant Status</b> and remove the{" "}
                          <b>Refuses OAC</b>. Are you sure you want to continue?
                        </>
                      }
                      confirmButtonText="Confirm"
                      cancelButtonText="Cancel"
                      buttons={[
                        { value: "yes", color: "success", label: "Yes" },
                        { value: "no", color: "error", label: "No" },
                        { value: "unknown", label: "Unknown" },
                      ]}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={7} style={{ marginLeft: "8px" }}>
                  <TextField
                    variant="outlined"
                    margin="none"
                    fullWidth
                    label="Anticoagulants"
                    name="anticoag"
                    inputProps={{ maxLength: 50 }}
                    value={newPatientHistoryInformation.anticoag}
                    onChange={(event) => {
                      const copy = cloneDeep(newPatientHistoryInformation);
                      copy.anticoag = event.target.value;
                      handleInputChange(copy);
                    }}
                  />
                </Grid>

                <Grid item xs={2} style={{ display: "flex" }}>
                  <RefusesOAC
                    refusesOAC={newPatientHistoryInformation.refusesOAC}
                    anticoagStatus={newPatientHistoryInformation.anticoagStatus}
                    handleRefuseOAC={(refusesOAC, removeAnticoagStatus) => {
                      const copy = cloneDeep(newPatientHistoryInformation);
                      copy.refusesOAC = refusesOAC;
                      if (removeAnticoagStatus) {
                        copy.anticoagStatus = "no";
                      }
                      handleInputChange(copy);
                    }}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-2 mb-2" />
            </div>

            <div className="patient-form__row">
              <div
                className="patient-form__info--header"
                style={{ color: "#175EC8" }}
              >
                STROKE HISTORY
              </div>

              <Grid container sx={{ paddingBottom: "5px" }}>
                <Grid item>
                  <ToggleButtonGroup
                    color="primary"
                    size="medium"
                    exclusive
                    style={{ height: "56px" }}
                    aria-label="StrokeHistoryStatus"
                    value={newPatientHistoryInformation.strokeHistoryStatus}
                    onChange={(event) => {
                      const copy = cloneDeep(newPatientHistoryInformation);
                      copy.strokeHistoryStatus = event.target.value;
                      handleInputChange(copy);
                    }}
                  >
                    <ToggleButton
                      value="yes"
                      color="error"
                      className="toggle-group-button"
                    >
                      Yes
                    </ToggleButton>
                    <ToggleButton
                      value="no"
                      color="success"
                      className="toggle-group-button"
                    >
                      No
                    </ToggleButton>
                    <ToggleButton
                      value="unknown"
                      className="toggle-group-button"
                    >
                      Unknown
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid item xs={7} style={{ marginLeft: "8px" }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    margin="none"
                    label="Description"
                    name="strokeHistory"
                    value={newPatientHistoryInformation.strokeHistory}
                    onChange={(event) => {
                      const copy = cloneDeep(newPatientHistoryInformation);
                      copy.strokeHistory = event.target.value;
                      handleInputChange(copy);
                    }}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-2 mb-2" />
            </div>
          </Grid>
        ) : (
          <div className="view-pdf__drawer-grid" style={{ width: "100%" }}>
            <div className="patient-form__row">
              <div
                className="patient-form__info--header"
                style={{
                  color: "#175EC8",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="mr-2">ANTICOAGULANT</span>
                {(newPatientHistoryInformation?.anticoagUpdatedAt ||
                  newPatientHistoryInformation?.anticoagUpdatedBy) && (
                  <AnticoagulantHistory
                    updatedAt={newPatientHistoryInformation?.anticoagUpdatedAt}
                    updatedBy={newPatientHistoryInformation?.anticoagUpdatedBy}
                  />
                )}
              </div>

              <Grid
                container
                columnSpacing={2}
                className="view-pdf__drawer-grid"
              >
                <GridListItem
                  title="ANTICOAGULANT STATUS"
                  subTitle={getAnticoagStatusValue()}
                  size="3"
                  isChip={getAnticoagStatusValue()}
                  chipProps={{
                    color:
                      ANTICOAGULANT_STATUS_COLOR_MAP[getAnticoagStatusValue()],
                  }}
                />

                <GridListItem
                  title="ANTICOAGULANTS"
                  subTitle={
                    newPatientHistoryInformation?.anticoag
                      ? capitalizeFirstLetter(
                          newPatientHistoryInformation?.anticoag
                            ?.toString()
                            ?.trim()
                        )
                      : "—"
                  }
                  size="3"
                />

                <GridListItem
                  title="REFUSES OAC"
                  subTitle={
                    newPatientHistoryInformation?.refusesOAC ? "Yes" : "No"
                  }
                  size="3"
                />
              </Grid>
            </div>

            <Divider className="mt-2 mb-2" />

            <div className="patient-form__row">
              <div
                className="patient-form__info--header"
                style={{ color: "#175EC8" }}
              >
                STROKE HISTORY
              </div>

              <Grid
                container
                columnSpacing={2}
                className="view-pdf__drawer-grid"
              >
                <GridListItem
                  title="STROKE STATUS"
                  subTitle={
                    newPatientHistoryInformation?.strokeHistoryStatus
                      ? capitalizeFirstLetter(
                          newPatientHistoryInformation?.strokeHistoryStatus.toString()
                        )
                      : "—"
                  }
                  size="3"
                  isChip={newPatientHistoryInformation?.strokeHistoryStatus}
                  chipProps={{
                    color:
                      STROKE_HISTORY_STATUS_COLOR_MAP[
                        newPatientHistoryInformation?.strokeHistoryStatus
                      ],
                  }}
                />

                <GridListItem
                  title="STROKE DESCRIPTION"
                  subTitle={
                    newPatientHistoryInformation?.strokeHistory
                      ? capitalizeFirstLetter(
                          newPatientHistoryInformation?.strokeHistory
                            ?.toString()
                            ?.trim()
                        )
                      : "—"
                  }
                  size="3"
                />
              </Grid>
            </div>

            <Divider className="mt-2 mb-2" />
          </div>
        )}

        <Grid
          container
          columnSpacing={2}
          className="view-pdf__drawer-grid"
          margin={0}
          paddingTop={0}
        >
          <div className="patient-form__row">
            <div
              className="patient-form__info--header"
              style={{ color: "#175EC8" }}
            >
              EHR DIAGNOSIS
            </div>

            <table className="diagnosis-table">
              {!isValidArray(
                newPatientHistoryInformation?.diagnosisHistory
              ) && (
                <div className="d-flex align-items-center">
                  No diagnosis data available.
                </div>
              )}

              {isValidArray(newPatientHistoryInformation?.diagnosisHistory) && (
                <>
                  <tr style={{ display: "table-row" }}>
                    <th className="table-title" style={{ color: "#757575" }}>
                      DESCRIPTION
                    </th>
                    <th className="table-title" style={{ color: "#757575" }}>
                      CODE
                    </th>
                    <th className="table-title" style={{ color: "#757575" }}>
                      DIAGNOSED DATE
                    </th>
                  </tr>
                  {newPatientHistoryInformation?.diagnosisHistory?.map(
                    (diagnosis, index) => (
                      <tr
                        key={`billing-${index}`}
                        style={{ display: "table-row" }}
                      >
                        <td className="table-value">
                          {diagnosis?.description?.length > 50 ? (
                            <Tooltip
                              title={diagnosis?.description || "-"}
                              placement="top"
                            >
                              <span style={{ cursor: "default" }}>
                                {diagnosis?.description
                                  ? truncate(diagnosis?.description, 50)
                                  : "-"}
                              </span>
                            </Tooltip>
                          ) : (
                            <span style={{ cursor: "default" }}>
                              {diagnosis?.description || "-"}
                            </span>
                          )}
                        </td>
                        <td className="table-value">{diagnosis.code || "-"}</td>
                        <td className="table-value">
                          {diagnosis?.diagnosedAt
                            ? formatDateTime(
                                diagnosis.diagnosedAt,
                                "MM/DD/YYYY hh:mm:ss A"
                              )
                            : "-"}
                        </td>
                      </tr>
                    )
                  )}
                </>
              )}
            </table>

            <Divider className="mt-2 mb-2" />
          </div>
        </Grid>

        <PatientDiagnosis isEditMode={isEditMode} />
      </Grid>
      {isEditMode && (
        <div
          style={{ textAlign: "right", gap: "10px" }}
          className="d-flex flex-end"
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              handleCancel();
              setIsEditMode(false);
            }}
          >
            Cancel
          </Button>
          <SubmitButton
            variant="contained"
            color="secondary"
            handleClick={handleSubmit}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default PatientHistoryForm;
